import { ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Popover from '@givebutter/primitives/popover';
import { useToast } from '@givebutter/primitives/toast';
import { faHashtag } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createTags } from 'api/tags';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface ContactTagCreateFormValues {
    tagLabel: string;
}

interface ContactTagCreateFormPopoverProps
    extends Pick<Popover.PopoverContentProps, 'align' | 'side' | 'sideOffset'> {
    children: ReactNode;
}

const ContactTagCreateFormPopover = ({
    children,
    align = 'end',
    side = 'bottom',
    sideOffset = 8,
}: ContactTagCreateFormPopoverProps) => {
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();
    const { error, success } = useToast();
    const methods = Form.useGivebutterForm({ defaultValues: { tagLabel: '' } });
    const {
        control,
        formState: { errors },
        handleSubmit,
        path,
        watch,
        setError,
        reset,
        resetFormButton,
    } = methods;
    const name = watch('tagLabel');

    const { mutate: submit, isPending: isLoading } = useMutation<
        unknown,
        GivebutterAxiosError,
        ContactTagCreateFormValues
    >({
        mutationFn: values => createTags(accountId, { tags: [values.tagLabel], type: 'contact' }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.tags(accountId) });
            success('Tag created!');
            reset();
        },
        onError: e => {
            const errors = e.response?.data.errors;

            if (errors) {
                for (let i in errors) {
                    setError('tagLabel', { message: errors[i][0] });
                }
            } else {
                error('Unable to create tag');
            }

            resetFormButton();
        },
    });

    return (
        <Popover.Root
            onOpenChange={isOpen => {
                if (!isOpen) reset();
            }}
        >
            {children}
            <Popover.Portal>
                <Popover.Content
                    align={align}
                    side={side}
                    sideOffset={sideOffset}
                    className="max-w-lg px-3.5 py-2.5"
                >
                    <Form.Provider {...methods}>
                        <form
                            onSubmit={handleSubmit(values => submit(values))}
                            className="flex flex-col gap-2"
                        >
                            <Form.Container>
                                <Form.Label name={path('tagLabel')} className="sr-only">
                                    Tag Name
                                </Form.Label>
                                <Form.TextInputRoot control={control} name="tagLabel">
                                    <Form.TextInputForm autoComplete="off" placeholder="Tag name" />
                                </Form.TextInputRoot>
                                <Form.Error name={path('tagLabel')} />
                            </Form.Container>
                            {!!name && !errors.tagLabel ? (
                                <Form.Container>
                                    <Form.SubmitButton
                                        variant="none"
                                        color="primary"
                                        size="md"
                                        disabled={isLoading}
                                        isSubmitting={isLoading}
                                        className="justify-start font-medium"
                                    >
                                        <span>{isLoading ? 'Creating' : 'Create'}</span>
                                        <div className="flex items-center gap-1">
                                            <FontAwesomeIcon
                                                icon={faHashtag}
                                                className="text-gray-400"
                                                size="xs"
                                            />
                                            <span className="text-gray-900">{name}</span>
                                        </div>
                                    </Form.SubmitButton>
                                </Form.Container>
                            ) : null}
                        </form>
                    </Form.Provider>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
};

const ContactTagCreateFormPopoverTrigger = Popover.Trigger;

const Root = ContactTagCreateFormPopover;
const Trigger = ContactTagCreateFormPopoverTrigger;

export {
    ContactTagCreateFormPopover,
    ContactTagCreateFormPopoverTrigger,
    //
    Root,
    Trigger,
};
